'use strict';

import { fh_polygon } from '@acenv/fh-3d-viewer';
import { cn_building } from '../../model/cn_building';
import { cn_scene } from '../../model/cn_scene';
import { cn_storey } from '../../model/cn_storey';
import { cn_vertex } from '../../model/cn_vertex';
import { CN_INNER, CN_MIDDLE, CN_OUTER, cn_wall } from '../../model/cn_wall';
import { cn_wall_type } from '../../model/cn_wall_type';
import { cn_add, cn_box, cn_dist, cn_dot, cn_intersect_line, cn_middle, cn_mul, cn_normal, cn_normalize, cn_size, cn_sub, cnx_clone } from '../cn_utilities';
import { cn_opening_type } from '../../model/cn_opening_type';
import { cn_opening } from '../../model/cn_opening';
import { cn_material } from '../../model/cn_material';
import { fh_xml } from '../fh_xml';
import { cn_vector_data, cn_vector_layer, cn_vector_path } from '../../model/cn_vector_data';
import { cn_background_map } from '../../model/cn_background_map';

//***********************************************************************************
//***********************************************************************************
//**** WISEBIM importer
//***********************************************************************************
//***********************************************************************************

export function cn_read_abyla(abula_dom) {
    var reader = new cn_abyla_reader();
    reader.parse(abula_dom);
    return reader._building;
}

class cn_abyla_paroi {
    constructor(v0, v1, piece, paroi) {
        this.vertices = [v0, v1];
        this.direction = cn_sub(v1.position, v0.position);
        this.size = cn_normalize(this.direction);
        this.piece = piece;
        this.paroi = paroi;
        this.parallel_segments = [];
    }
}

const ABYLA_WALL_SAMPLING = 0.1;

export class cn_abyla_reader {
    constructor() {
        this._building = null;
    }

    //***********************************************************************************
    //**** Main function
    //***********************************************************************************
    parse(abyla_dom_true) {
        let segment_index = 0;
        let abyla_dom = fh_xml.from_dom(abyla_dom_true.children[0]);
        this._building = new cn_building();
        this._building.storeys = [];
        this._wall_types = [];
        let wt = this._building.get_wall_types().find(wt => wt.free);
        let a_storeys = abyla_dom.find_descendants("ETAGE");
        let storeys = [];
        for (let s of a_storeys) {
            storeys.push({ index: parseInt(s.get_attribute("niveau_etage")), dom: s });
        }
        storeys.sort((a, b) => a.index - b.index);
        let z0 = -1;
        storeys.forEach((st, i) => {
            if (i == storeys.length - 1) return;
            let storey = new cn_storey(this._building);
            this._building.storeys.push(storey);
            storey.storey_index = st.index;
            storey.name = st.dom.get_attribute("name");
            storey.slab_height = 0.01 * parseFloat(st.dom.get_attribute("hauteur_reelle"));

            let background_map = this._building.new_background_map("", 100, 100);
            background_map.scale = 1;
            background_map.fixed_map = false;
            background_map.vector_data = new cn_vector_data(background_map);

            let layer = new cn_vector_layer(background_map);
            layer.label = "Extérieur";
            layer.color = "#000008";
            layer.visible = true;
            background_map.vector_data.layers.push(layer);
            let a_facades = st.dom.find_descendants("PAROI_FACADE");
            let segments = [];
            for (let a_f of a_facades) {
                let p1 = cn_mul([parseFloat(a_f.get_attribute("x1")), parseFloat(a_f.get_attribute("y1"))], 0.01);
                let p2 = cn_mul([parseFloat(a_f.get_attribute("x2")), parseFloat(a_f.get_attribute("y2"))], 0.01);
                let v1 = storey.scene.vertices.find(v => cn_dist(v.position, p1) < 0.015);
                if (!v1) {
                    v1 = new cn_vertex(p1, false, storey.scene);
                    storey.scene.vertices.push(v1);
                }
                let v2 = storey.scene.vertices.find(v => cn_dist(v.position, p2) < 0.015);
                if (!v2) {
                    v2 = new cn_vertex(p2, false, storey.scene);
                    storey.scene.vertices.push(v2);
                }
                segments.push(new cn_abyla_paroi(v1, v2, null, a_f));
                let e = new cn_vector_path(layer);
                e.closed_contour = false;
                e.vertices = [p1, p2];
                layer.elements.push(e);
                a_f.index = segment_index;
                segment_index++;
            }

            let a_pieces = st.dom.find_descendants("PIECE");
            a_pieces.forEach((a_piece, index_piece) => {
                layer = new cn_vector_layer(background_map);
                layer.label = "Pièce " + index_piece;
                layer.color = "#000000";
                layer.visible = true;
                background_map.vector_data.layers.push(layer);
                let space_vertices = [];
                let a_murs = a_piece.find_descendants("PAROI_PIECE");
                for (let a_f of a_murs) {
                    let p1 = cn_mul([parseFloat(a_f.get_attribute("x1")), parseFloat(a_f.get_attribute("y1"))], 0.01);
                    let p2 = cn_mul([parseFloat(a_f.get_attribute("x2")), parseFloat(a_f.get_attribute("y2"))], 0.01);
                    let v1 = space_vertices.find(v => cn_dist(v.position, p1) < 0.015);
                    if (!v1) {
                        v1 = new cn_vertex(p1, false, storey.scene);
                        storey.scene.vertices.push(v1);
                        space_vertices.push(v1);
                    }
                    let v2 = space_vertices.find(v => cn_dist(v.position, p2) < 0.015);
                    if (!v2) {
                        v2 = new cn_vertex(p2, false, storey.scene);
                        storey.scene.vertices.push(v2);
                        space_vertices.push(v2);
                    }

                    segments.push(new cn_abyla_paroi(v1, v2, a_piece, a_f));
                    a_f.index = segment_index;
                    segment_index++;

                    let e = new cn_vector_path(layer);
                    e.closed_contour = false;
                    e.vertices = [p1, p2];
                    layer.elements.push(e);
                }
            });

            let wall_types = [];
            segments.filter(segment => segment.piece).forEach(segment => {
                let start_points = [];
                if (segment.size > ABYLA_WALL_SAMPLING) {
                    for (var x = ABYLA_WALL_SAMPLING * 0.5; x < segment.size; x += ABYLA_WALL_SAMPLING) {
                        start_points.push(x);
                    }
                }
                else
                    start_points.push(0.5 * segment.size);
                let direction = cn_mul(cn_normal(segment.direction), -1);
                segment["rt"] = [];
                start_points.forEach(x => {
                    let origin = cn_add(segment.vertices[0].position, cn_mul(segment.direction, x));
                    let seg = this._raytrace(origin, direction, segment, segments);
                    segment["rt"].push(seg);
                    let other_segment = seg.segment;
                    if (other_segment) {
                        if (!segment.parallel_segments.includes(other_segment))
                            segment.parallel_segments.push(other_segment);
                        if (!other_segment.parallel_segments.includes(segment))
                            other_segment.parallel_segments.push(segment);
                    }
                });
            });

            segments.filter(segment => segment.piece).forEach(segment => {
                if (segment.parallel_segments.length == 0) {
                    let wall_type = this._find_wall_type(0);
                    if (!wall_types.includes(wall_type)) wall_types.push(wall_type);
                    let wall = new cn_wall(segment.vertices[0], segment.vertices[1], wall_type, CN_OUTER, storey.scene);
                    wall["abyla_segment"] = segment;
                    storey.scene.walls.push(wall);
                    return;
                }
                segment.parallel_segments.forEach(ps => {
                    ps["x0"] = cn_dot(segment.direction, cn_sub(ps.vertices[1].position, segment.vertices[0].position));
                    ps["x1"] = cn_dot(segment.direction, cn_sub(ps.vertices[0].position, segment.vertices[0].position));
                });
                segment.parallel_segments.sort((a, b) => a["x0"] - b["x0"]);
                let vertex_prec = segment.vertices[0];
                let normal = cn_normal(segment.direction);
                segment.parallel_segments.forEach((ps, psi) => {
                    let next_vertex = new cn_vertex(cn_add(segment.vertices[0].position, cn_mul(segment.direction, ps["x1"])), false, storey.scene);
                    if (psi == segment.parallel_segments.length - 1)
                        next_vertex = segment.vertices[1];
                    if (ps.paroi.index < segment.paroi.index) {
                        let thickness = Math.abs(cn_dot(normal, cn_sub(ps.vertices[0].position, segment.vertices[0].position)));
                        let wall_type = this._find_wall_type(thickness);
                        if (!wall_types.includes(wall_type)) wall_types.push(wall_type);
                        if (!storey.scene.vertices.includes(vertex_prec)) storey.scene.vertices.push(vertex_prec);
                        if (!storey.scene.vertices.includes(next_vertex)) storey.scene.vertices.push(next_vertex);
                        let wall = new cn_wall(vertex_prec, next_vertex, wall_type, CN_OUTER, storey.scene);
                        wall["abyla_segment"] = segment;
                        storey.scene.walls.push(wall);
                    }
                    vertex_prec = next_vertex;
                });

            });


            //*** Merge walls that match along */
            while (true) {
                let change = false;
                wall_types.forEach(wall_type => {
                    let walls = storey.scene.walls.filter(w => w.wall_type == wall_type);
                    if (walls.length < 2) return;
                    walls.some(wall0 => {
                        let direction = cn_sub(wall0.vertices[1].position, wall0.vertices[0].position);
                        let x0 = cn_normalize(direction);
                        let p0 = wall0.vertices[0].position;
                        let normal = cn_normal(direction);
                        /**  @type { cn_wall } */
                        let best_wall = null;
                        let best_x = x0 + 0.5;
                        walls.forEach(w => {
                            if (w == wall0) return;
                            if (Math.abs(cn_dot(normal, cn_sub(w.vertices[0].position, p0))) > 0.01) return;
                            if (Math.abs(cn_dot(normal, cn_sub(w.vertices[1].position, p0))) > 0.01) return;
                            let xx0 = cn_dot(direction, cn_sub(w.vertices[0].position, p0));
                            if (xx0 > best_x || xx0 < 0) return;
                            let xx1 = cn_dot(direction, cn_sub(w.vertices[1].position, p0));
                            if (xx1 < xx0) return;
                            best_x = xx0;
                            best_wall = w;
                        });
                        if (!best_wall) return false;

                        let idx = storey.scene.walls.indexOf(best_wall);
                        storey.scene.walls.splice(idx, 1);
                        wall0.vertices[1] = best_wall.vertices[1];
                        change = true;
                        return true;
                    });
                });
                if (!change) break;
            }


            storey.scene.full_update(true);

            background_map.initialize_vector_data();
            background_map.offset = cn_add(background_map.vector_data_position, cn_mul(background_map.image_size, 0.5));
            storey.background_maps.push(background_map);
        });
        console.log(storeys);
    }

    _raytrace(origin, direction, start_segment, segments) {
        let max_distance = 0.5;
        let best_impact = null;
        segments.forEach(s => {
            if (s == start_segment) return;
            let normal = cn_normal(s.direction);
            let den = cn_dot(direction, normal);
            if (Math.abs(den) < 0.001) return;
            let lambda = cn_dot(normal, cn_sub(s.vertices[0].position, origin)) / den;
            if (lambda < 0 || lambda >= max_distance) return;
            let p = cn_add(origin, cn_mul(direction, lambda));
            let x = cn_dot(s.direction, cn_sub(p, s.vertices[0].position));
            if (x < 0 || x > s.size) return;
            max_distance = lambda;
            if (den > 0.99) best_impact = s;
        });
        return { distance: max_distance, segment: best_impact, origin: origin, direction: direction };
    }

    _find_wall_type(thickness) {
        let wall_type = this._building.get_wall_types().find(wt => wt.thickness == thickness);
        if (wall_type) return wall_type;
        wall_type = new cn_wall_type(this._building);
        wall_type.category = 'generic';
        wall_type.layers = [cn_material.material_by_code(this._building, 'unknown', thickness)];
        this._building.add_element_type(wall_type);
        return wall_type;
    }
}


